import React from 'react';
import './Products.css';
import './ProductRecommendation.css';
import Footer from './Footer';
import ContactRecommendation from './ContactRecommendation';
import SEO from './SEO';

const ProductRecommendation = () => {
  const journeySteps = [
    {
      id: 1,
      header: 'DISCOVERY',
      title: 'New Customer',
      description: 'Lands on your website for the first time with no purchase history',
      pastel: '#E6F5E6',
      side: 'left'
    },
    {
      id: 2,
      header: 'POPULAR PRODUCTS',
      title: 'Recommended Items',
      description: 'The most popular products sold on your website are showcased to new visitors',
      pastel: '#EEECF8',
      side: 'right'
    },
    {
      id: 3,
      header: 'FIRST PURCHASE',
      title: 'Behavioral Data',
      description: 'Makes a purchase and the system captures behavioral data for future recommendations',
      pastel: '#E6F5F5',
      side: 'left'
    },
    {
      id: 4,
      header: 'PERSONALIZATION',
      title: 'Tailored Recommendations',
      description: 'Collaborative filtering recommends products based on purchase history and similar users',
      pastel: '#F8F5E6',
      side: 'right'
    }
  ];

  const highlights = [
    {
      id: 1,
      title: 'ML-Powered Recommendations',
      description: 'Machine learning algorithms learn from behavioral data to surface increasingly relevant suggestions over time, adapting to each customer\'s preferences.',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#667eea'
    },
    {
      id: 2,
      title: 'Intelligent, Data-Driven Recommendations',
      description: 'Analyze browsing habits and purchase history to curate product suggestions aligned with user intent, personalizing the shopping experience.',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      color: '#f5576c'
    },
    {
      id: 3,
      title: 'Hybrid Recommendation Engine',
      description: 'Rule-based, content-based, and collaborative filtering combined for maximum accuracy and coverage across new and returning customers.',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      color: '#4facfe'
    }
  ];

  return (
    <>
      <SEO 
        title="AI Product Recommendation Engine - ML-Powered E-Commerce Personalization"
        description="Boost conversions with our AI-powered recommendation engine. Machine learning, collaborative filtering, and content-based recommendations that personalize shopping experiences. Supports MySQL, PostgreSQL, MongoDB, and more."
        keywords="AI recommendation engine, product recommendations, machine learning, collaborative filtering, e-commerce personalization, ML recommendations, shopping personalization, product suggestions"
        url="/recommendation-engine"
        product={{
          name: "AI Product Recommendation Engine",
          description: "ML-powered recommendation system using collaborative filtering, content-based filtering, and rule-based recommendations to personalize e-commerce experiences.",
          operatingSystem: "Web API",
          features: "Machine Learning, Collaborative Filtering, Content-based Filtering, Real-time Recommendations, Multi-database Support (MySQL, PostgreSQL, MongoDB, Oracle)"
        }}
      />
      <section
        className="product-recommendation-hero"
        style={{ '--hero-bg-image': `url(${process.env.PUBLIC_URL || ''}/images/ai_recommedation.jpg)` }}
      >
        <div className="container">
          <div className="product-recommendation-hero-content">
            <h4>AI-Powered Product Discovery</h4>
            <h1>Recommendation <span style={{ color: '#f54e4d' }}>Engine</span> for E-Commerce</h1>
            <p className="product-recommendation-hero-subtitle">
              A well-developed recommendation system helps businesses improve their shopper's experience, 
              resulting in better customer acquisition and retention.
            </p>
            <div className="product-recommendation-hero-cta">
              <button 
                className="cta-button primary" 
                onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
              >
                CONTACT SALES
              </button>
              <button 
                className="cta-button secondary" 
                onClick={() => window.open('https://recommendation.fractallogy.com/', '_blank')}
              >
                Get a Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="container product-recommendation-intro-heading-outer">
        <h2 className="product-recommendation-intro-heading">Intelligent, data-driven recommendations that personalize the shopping experience.</h2>
        <p className="product-recommendation-intro-lead">ML-powered recommendations powered by a hybrid engine combining rule-based, content-based, and collaborative filtering.</p>
      </div>

      <section 
        className="product-recommendation-intro" 
        style={{ '--intro-bg-image': `url(${process.env.PUBLIC_URL || ''}/images/background_features.jpg)` }}
      >
        <div className="container">
          <div className="product-recommendation-intro-content">
            <div className="product-recommendation-features-grid">
              {highlights.map((item) => {
                const hex = item.color.replace('#', '');
                const r = parseInt(hex.substr(0, 2), 16);
                const g = parseInt(hex.substr(2, 2), 16);
                const b = parseInt(hex.substr(4, 2), 16);
                return (
                  <div 
                    key={item.id} 
                    className="product-recommendation-feature-card"
                    style={{
                      '--glow-color': item.color,
                      '--glow-rgb': `${r}, ${g}, ${b}`
                    }}
                  >
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <div className="feature-card-accent" style={{ background: item.gradient }}></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="recommendation-personalization">
        <div className="container">
          <div className="recommendation-section-header">
            <h2>Personalize the Shopping Experience</h2>
          </div>
          <div className="recommendation-how-it-works">
            <p>
              Recommendation engines analyze a user's browsing habits and purchase history to curate 
              product suggestions aligned with user intent. Product listings are also tailored to the 
              user's location, with pricing displayed in the customer's local currency plus applicable 
              shipping fees.
            </p>
          </div>
        </div>
      </section>

      <section className="recommendation-journey">
        <div className="container">
          <div className="recommendation-section-header">
            <h2>Customer Journey Flow</h2>
          </div>
          <div className="journey-timeline">
            <div className="journey-axis"></div>
            {journeySteps.map((step) => (
              <div
                key={step.id}
                className={`journey-timeline-item journey-side-${step.side}`}
              >
                <div
                  className="journey-timeline-card"
                  style={{ '--card-pastel': step.pastel }}
                >
                  <div className="journey-card-header">{step.header}</div>
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
                <div className="journey-connector-line" aria-hidden="true"></div>
                <div className="journey-diamond" aria-hidden="true"></div>
                <div className="journey-connector-line" aria-hidden="true"></div>
                <div
                  className="journey-phase-marker"
                  style={{ '--marker-pastel': step.pastel }}
                >
                  Step {step.id}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Connectivity Section */}
      <section className="data-connectivity-section">
        <div className="container">
          <div className="data-connectivity-header">
            <h2>Connect Any Data Source</h2>
            <p>Our recommendation engine seamlessly integrates with your existing data infrastructure. Whether you're using MySQL, PostgreSQL, MongoDB, or cloud services like AWS and Google Cloud, we connect to all major databases and platforms to deliver personalized recommendations in real-time.</p>
          </div>
          
          <div className="data-connectivity-diagram">
            {/* Left column - Data Input Sources */}
            <div className="infographic-column infographic-column-left">
              <div className="infographic-card infographic-card-left" style={{ '--pastel': '#8B4A5E', '--solid': '#6B3548' }}>
                <span className="infographic-card-text" style={{ color: '#fff' }}>MySQL</span>
                <div className="infographic-card-tab infographic-tab-right">
                  <span>01</span>
                </div>
              </div>
              <div className="infographic-card infographic-card-left" style={{ '--pastel': '#F28B82', '--solid': '#E57373' }}>
                <span className="infographic-card-text">JSON</span>
                <div className="infographic-card-tab infographic-tab-right">
                  <span>02</span>
                </div>
              </div>
              <div className="infographic-card infographic-card-left" style={{ '--pastel': '#81D4D8', '--solid': '#4DB6AC' }}>
                <span className="infographic-card-text">API</span>
                <div className="infographic-card-tab infographic-tab-right">
                  <span>03</span>
                </div>
              </div>
              <div className="infographic-card infographic-card-left" style={{ '--pastel': '#C5B3E3', '--solid': '#9575CD' }}>
                <span className="infographic-card-text">MSSQL</span>
                <div className="infographic-card-tab infographic-tab-right">
                  <span>04</span>
                </div>
              </div>
            </div>

            {/* Central dashed circle */}
            <div className="infographic-center">
              <div className="infographic-center-inner">
                <span className="infographic-center-title">RECOMMENDATION</span>
                <span className="infographic-center-title">ENGINE</span>
              </div>
              <div className="infographic-dots">
                <span className="infographic-dot" style={{ background: '#8B4A5E' }}></span>
                <span className="infographic-dot" style={{ background: '#F9CE58' }}></span>
                <span className="infographic-dot" style={{ background: '#81D4D8' }}></span>
                <span className="infographic-dot" style={{ background: '#F28B82' }}></span>
                <span className="infographic-dot" style={{ background: '#C5B3E3' }}></span>
                <span className="infographic-dot" style={{ background: '#4DB6AC' }}></span>
                <span className="infographic-dot" style={{ background: '#9575CD' }}></span>
                <span className="infographic-dot" style={{ background: '#F9CE58' }}></span>
              </div>
            </div>

            {/* Right column - Data Output Sources */}
            <div className="infographic-column infographic-column-right">
              <div className="infographic-card infographic-card-right" style={{ '--pastel': '#F9CE58', '--solid': '#F9A825' }}>
                <div className="infographic-card-tab infographic-tab-left">
                  <span>01</span>
                </div>
                <span className="infographic-card-text">CSV</span>
              </div>
              <div className="infographic-card infographic-card-right" style={{ '--pastel': '#81D4D8', '--solid': '#4DB6AC' }}>
                <div className="infographic-card-tab infographic-tab-left">
                  <span>02</span>
                </div>
                <span className="infographic-card-text">MongoDB</span>
              </div>
              <div className="infographic-card infographic-card-right" style={{ '--pastel': '#F28B82', '--solid': '#E57373' }}>
                <div className="infographic-card-tab infographic-tab-left">
                  <span>03</span>
                </div>
                <span className="infographic-card-text">Oracle</span>
              </div>
              <div className="infographic-card infographic-card-right" style={{ '--pastel': '#C5B3E3', '--solid': '#9575CD' }}>
                <div className="infographic-card-tab infographic-tab-left">
                  <span>04</span>
                </div>
                <span className="infographic-card-text">PostgreSQL</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactRecommendation />
      <Footer />
    </>
  );
};

export default ProductRecommendation;
